import { IconButton, Typography } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import { CREDIT_KINDS, CREDIT_SCHEDULES, type CreditPurchaseConfig } from '@vy/types';
import { withDefault } from '@vy/utils';
import React, { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { CloseIcon } from '../../../../../components/icons';
import { AddCreditDrawer } from '../AddCreditDrawer';
import { buildCreditPurchaseConfig, extractCreditFormFromCreditPurchaseConfig } from '../AddCreditDrawer';
import type { AddCreditForm } from '../AddCreditDrawer';
import type { NewPlanForm } from '../CreatePlanForm/create-plan-form-types/new-plan-form';
import type { PlanInputForm } from '../CreatePlanForm/create-plan-form-types/plan-input-form';
import { ChargesEditDataGrid } from '../ChargesEditDataGrid';
import { ModifyButton } from '../ModifyButton';
import { useDialog } from '../../../../../hooks/use-dialog';
import { toCreditPurchaseItem, type CreditGrantView } from './credit-grant-view';
import { applyCreditEdit, applyCreditRemoval } from './apply-credit-mutation';

function formatSchedule(schedule: CreditPurchaseConfig['schedule']): string {
  switch (schedule) {
    case CREDIT_SCHEDULES.ONE_TIME:
      return 'One-time';
    case CREDIT_SCHEDULES.RECURRING_MONTHLY:
      return 'Monthly';
    case CREDIT_SCHEDULES.RECURRING_QUARTERLY:
      return 'Quarterly';
    case CREDIT_SCHEDULES.RECURRING_YEARLY:
      return 'Yearly';
    default:
      return schedule;
  }
}

type EditCellProps = {
  row: CreditGrantView;
};

const EditCell: React.FC<EditCellProps> = ({ row }) => {
  const { getValues, setValue } = useFormContext<PlanInputForm, NewPlanForm>();
  const { isDialogOpen, handleOpen, handleClose } = useDialog();

  const handleEditCredit = (data: AddCreditForm) => {
    const sources = {
      credits: withDefault(getValues('credits'), []),
      fixedProducts: withDefault(getValues('fixedProducts'), []),
    };
    const result = applyCreditEdit(sources, row, buildCreditPurchaseConfig(data));

    setValue(result.field, result.value, { shouldDirty: true });
  };

  return (
    <>
      <ModifyButton handleOpen={handleOpen} isDialogOpen={isDialogOpen} />

      {isDialogOpen && (
        <AddCreditDrawer
          open={isDialogOpen}
          onClose={handleClose}
          defaultValues={extractCreditFormFromCreditPurchaseConfig(toCreditPurchaseItem(row))}
          onAddCredit={handleEditCredit}
          title="Edit credit"
          submitLabel="Save"
        />
      )}
    </>
  );
};

type RemoveCellProps = {
  row: CreditGrantView;
};

const RemoveCell: React.FC<RemoveCellProps> = ({ row }) => {
  const { getValues, setValue } = useFormContext<PlanInputForm, NewPlanForm>();

  const handleRemove = () => {
    const sources = {
      credits: withDefault(getValues('credits'), []),
      fixedProducts: withDefault(getValues('fixedProducts'), []),
    };
    const result = applyCreditRemoval(sources, row);

    setValue(result.field, result.value, { shouldDirty: true });
  };

  return (
    <IconButton
      size="small"
      onClick={handleRemove}
      data-vy={`credits-edit-table-row-${row.index}-remove`}
      sx={{ padding: 0 }}
    >
      <CloseIcon />
    </IconButton>
  );
};

type CreditsEditTableProps = {
  rows: CreditGrantView[];
  readOnly?: boolean;
};

export const CreditsEditTable: React.FC<CreditsEditTableProps> = ({ rows, readOnly = false }) => {
  const columns = useMemo((): GridColDef<CreditGrantView>[] => {
    const baseColumns: GridColDef<CreditGrantView>[] = [
      {
        field: 'name',
        headerName: 'Name',
        flex: 2,
        sortable: false,
        minWidth: 120,
        renderCell: ({ row }) => (
          <Typography variant="body2" noWrap data-vy={`credits-edit-table-row-${row.index}`}>
            {row.name}
          </Typography>
        ),
      },
      {
        field: 'creditKind',
        headerName: 'Type',
        flex: 1,
        sortable: false,
        minWidth: 80,
        valueGetter: (_value, row) => (row.creditKind === CREDIT_KINDS.MONETARY ? 'Amount ($)' : 'Units'),
      },
      {
        field: 'amount',
        headerName: 'Amount',
        flex: 1,
        sortable: false,
        minWidth: 80,
      },
      {
        field: 'schedule',
        headerName: 'Schedule',
        flex: 1,
        sortable: false,
        minWidth: 100,
        valueGetter: (_value, row) => formatSchedule(row.schedule),
      },
      {
        field: 'expiryDate',
        headerName: 'Expiry',
        flex: 1,
        sortable: false,
        minWidth: 100,
        valueGetter: (_value, row) => {
          if (row.expiryDate == null) return '—';
          const d = row.expiryDate;
          return d instanceof Date ? d.toLocaleDateString() : new Date(d as string).toLocaleDateString();
        },
      },
    ];

    if (readOnly) {
      return baseColumns;
    }

    return [
      ...baseColumns,
      {
        field: 'edit',
        headerName: '',
        cellClassName: 'justify-end items-center',
        width: 130,
        sortable: false,
        renderCell: ({ row }) => <EditCell row={row} />,
      },
      {
        field: 'remove',
        headerName: '',
        width: 40,
        sortable: false,
        cellClassName: 'justify-center',
        renderCell: ({ row }) => <RemoveCell row={row} />,
      },
    ];
  }, [readOnly]);

  return (
    <ChargesEditDataGrid
      columns={columns}
      rows={rows}
      getRowId={(row) => `${row.source}-${row.index}`}
      columnHeaderHeight={36}
      rowHeight={56}
      disableColumnMenu
      disableRowSelectionOnClick
      hideFooter
    />
  );
};
